import { useContext, useState } from "react";
import { format } from "date-fns";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import UiContext from "../../../../store/ui-context";
import useIsMobile from "../../../../hooks/useIsMobile";
import ChecklistProblemDialog from "../checklist/ChecklistProblemDialog";
import ChecklistRowDetail from "../checklist/ChecklistRowDetail";
import {
  findMissingNewProblems,
  findProblem,
  isIgnoredProblem,
  isNewProblem,
  isSameGambar,
} from "../../utils/checklistProblem";

// One checklist card of the audit (a bagian of the vehicle): a checkbox column and a DESKRIPSI column.
// Checking a row with an open problem asks a note + photo first; an unchecked row without an open problem is a new
// problem and needs a note + photo before the card can be saved.
const CheckList = ({
  labelGrup,
  pengecekanDetilData,
  setPengecekanDetilData,
  title,
  disabled,
  onClickSave,
  onClickDeskripsi,
  onClickReload,
  loading,
  listProblemData,
  folderName,
}) => {
  const uiContext = useContext(UiContext);
  const isMobile = useIsMobile();
  // the note + gambar dialog: { detil, mode: "check" | "new-problem", problem }
  const [dialogTarget, setDialogTarget] = useState(null);
  // set when a save was blocked: the new problems that still miss note / gambar get a red hint
  const [highlightMissing, setHighlightMissing] = useState(false);
  // rows whose detail was opened / closed by hand: { keterangan: true | false }
  const [expandedRows, setExpandedRows] = useState({});

  const detilData = pengecekanDetilData
    ? pengecekanDetilData
        .filter((el) => el.label_grup === labelGrup)
        .sort((a, b) => a.urutan - b.urutan)
    : [];

  // merges `patch` into the detil `id`
  const updateDetil = (id, patch) => {
    setPengecekanDetilData(
      pengecekanDetilData.map((el) => (el.id === id ? { ...el, ...patch } : el))
    );
  };

  // a box was (un)checked. Checking a row that has a problem first asks note + gambar in the dialog.
  const handleToggleCek = (detil, checked) => {
    const problem = findProblem(listProblemData, detil);

    if (checked && problem) {
      setDialogTarget({ detil, mode: "check", problem });
    } else {
      updateDetil(detil.id, { cek: checked ? 1 : 0 });
    }
  };

  const handleOnConfirmDialog = ({ note, gambar }) => {
    const { detil, mode } = dialogTarget;
    updateDetil(
      detil.id,
      mode === "check" ? { cek: 1, note, gambar } : { note, gambar }
    );
    setDialogTarget(null);
  };

  // Save: an unchecked row without an open problem is a new problem, it needs a note and a gambar first
  const handleOnClickSave = () => {
    const missing = findMissingNewProblems({
      detils: detilData,
      listProblemData,
    });
    if (missing.length > 0) {
      setHighlightMissing(true);
      uiContext.showErrorMsg(
        "Error",
        `Problem baru wajib diisi note dan gambar: ${missing
          .map((el) => el.keterangan)
          .join(", ")}`
      );
      return;
    }
    setHighlightMissing(false);

    onClickSave(detilData);
  };

  const cekBodyTemplate = (row) => (
    <div className="row-center-center">
      <Checkbox
        disabled={disabled}
        className="!p-0"
        checked={row.cek === 1}
        onChange={(e) => handleToggleCek(row, e.target.checked)}
      />
    </div>
  );

  const descriptionBodyTemplate = (row) => {
    const { keterangan } = row;

    // open problem of this row (shown with a warning icon)
    const problem = findProblem(listProblemData, row);

    // the note / photo of the row itself; the ones the problem already shows are left out
    let note = row.note ?? "";
    let image = row.gambar;
    if (problem) {
      if (note && problem.note && note.trim() === problem.note.trim()) {
        note = "";
      }
      if (isSameGambar(image, problem.gambar)) image = null;
    }

    const isMissingNewProblem =
      highlightMissing &&
      findMissingNewProblems({ detils: [row], listProblemData }).length > 0;

    const problemDayCount = problem
      ? Math.floor(
          (new Date() - new Date(problem.tanggal)) / (1000 * 60 * 60 * 24)
        )
      : 0;
    const problemSince = problem
      ? `Problem sejak ${format(new Date(problem.tanggal), "dd/MM/yyyy")}${
          problemDayCount > 0 ? ` (${problemDayCount} hari)` : ""
        }`
      : "";

    // an unchecked row that is a new problem: its note / photo are the new problem's
    const rowIsNewProblem =
      row.cek !== 1 && isNewProblem({ detil: row, listProblemData });

    const altName = keterangan.replace(" ", "_");

    // the detail (problem lama / the row's own note + photo) is closed on a phone: with many rows the photos make the
    // list too long to walk along the vehicle. Desktop starts open. Tap "Detail" to change it per row.
    const photoCount = [problem && problem.gambar, image].filter(Boolean).length;
    const noteCount = [problem && problem.note, note].filter(Boolean).length;
    // a problem with neither note nor gambar still needs to be reachable (at least the "sejak ... hari" line)
    const detailCount = photoCount + noteCount + (problem ? 1 : 0);
    const isRowExpanded = expandedRows[keterangan] ?? !isMobile;

    return (
      <div className="col-start-start gap-2 cursor-pointer w-full">
        <div className="row-between-start w-full gap-2">
          <span
            onClick={(e) => {
              if (onClickDeskripsi) onClickDeskripsi(e, row);
            }}
          >
            {keterangan}
          </span>
          {problem && (
            <span
              className="row-start-center gap-1 shrink-0"
              title={`${problemSince}${
                isIgnoredProblem(problem) ? " - diabaikan" : ""
              }`}
            >
              {isIgnoredProblem(problem) && (
                <span className="text-xs font-semibold text-slate-400">
                  Ignore
                </span>
              )}
              <i
                className={`pi pi-exclamation-triangle ${
                  isIgnoredProblem(problem) ? "text-slate-400" : "text-red-500"
                }`}
              ></i>
            </span>
          )}
        </div>

        {/* what the row has: tap to open / close, the counts tell what is inside without opening it */}
        {detailCount > 0 && (
          <button
            type="button"
            className="row-start-center gap-2 text-xs text-slate-500 bg-transparent border-none p-0 cursor-pointer"
            onClick={() =>
              setExpandedRows((prev) => ({
                ...prev,
                [keterangan]: !isRowExpanded,
              }))
            }
          >
            <i
              className={`pi ${
                isRowExpanded ? "pi-chevron-up" : "pi-chevron-down"
              } text-[10px]`}
            ></i>
            <span className="font-semibold">Detail</span>
            {photoCount > 0 && (
              <span className="row-start-center gap-1">
                <i className="pi pi-image text-[11px]"></i>
                {photoCount}
              </span>
            )}
            {noteCount > 0 && (
              <span className="row-start-center gap-1">
                <i className="pi pi-comment text-[11px]"></i>
                {noteCount}
              </span>
            )}
          </button>
        )}

        {detailCount > 0 && isRowExpanded && (
          <div className="col-start-start gap-2 w-full">
            {problem && (
              <ChecklistRowDetail
                className="rounded bg-red-100 px-2 py-1.5"
                label={`Problem lama - ${problemSince.replace("Problem ", "")}`}
                labelClassName="text-red-600"
                noteClassName="text-red-500"
                note={problem.note}
                gambar={problem.gambar}
                alt={`problem_${altName}`}
              />
            )}
            {(note || image) && (
              <ChecklistRowDetail
                label={rowIsNewProblem ? "Problem baru" : null}
                labelClassName="text-red-600"
                note={note}
                gambar={image}
                alt={`audit_${altName}`}
              />
            )}
          </div>
        )}

        {/* what still has to be filled in stays visible, also when the detail is closed */}
        {isMissingNewProblem && (
          <span
            className="text-xs text-red-600 font-semibold row-start-center gap-1 hover:underline"
            onClick={() =>
              setDialogTarget({
                detil: row,
                mode: "new-problem",
                problem: undefined,
              })
            }
          >
            <i className="pi pi-pencil text-xs"></i> Note & foto wajib diisi
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="col-start-start gap-2 w-full">
      <ChecklistProblemDialog
        visible={!!dialogTarget}
        mode={dialogTarget?.mode}
        detil={dialogTarget?.detil}
        problem={dialogTarget?.problem}
        folderName={folderName}
        onConfirm={handleOnConfirmDialog}
        onHide={() => setDialogTarget(null)}
      />
      <DataTable
        loading={loading}
        className="w-full"
        rowClassName={(data) => (data.cek ? "bg-green-50" : "bg-red-50")}
        header={
          <div className="row-between-center">
            <span className="text-sm"> {title.toUpperCase()} </span>
            <Button
              loading={loading}
              className="!p-0 max-h-[1.3rem] max-w-[1.3rem]"
              icon="pi pi-refresh"
              text
              rounded
              onClick={onClickReload}
            />
          </div>
        }
        footer={
          <div className="row-end-center">
            <Button
              loading={loading}
              disabled={disabled}
              icon="pi pi-save"
              label={`Save ${title}`}
              severity="success"
              onClick={handleOnClickSave}
            />
          </div>
        }
        value={detilData}
      >
        <Column
          header={<i className="pi pi-check"></i>}
          headerClassName="text-xs"
          className="w-[3rem] !p-0"
          alignHeader={"center"}
          body={cekBodyTemplate}
        />
        <Column
          header="DESKRIPSI"
          headerClassName="text-xs"
          className="border-l-2"
          body={descriptionBodyTemplate}
        />
      </DataTable>
    </div>
  );
};

export default CheckList;
